import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreateAPIRequest } from '../types/api';
import { createAPI } from '../services/apiService';
import { ReadmeUpload } from './ReadmeUpload';
import './AddAPI.css';
import './EnhancedAddAPI.css';

interface EnhancedFormData extends CreateAPIRequest {
  version?: string;
  documentation?: string;
  authentication?: string;
  rateLimit?: string;
  extractedFromReadme?: boolean;
  readmeAnalysisConfidence?: {
    name: number;
    description: number;
    url: number;
    tags: number;
    payloadDescription: number;
  };
  originalReadmeContent?: string;
}

interface FormErrors {
  name?: string;
  description?: string;
  url?: string;
  general?: string;
}

interface FieldConfidence {
  field: string;
  score: number;
  isManuallyEdited: boolean;
}

interface EnhancedAddAPIState {
  formData: EnhancedFormData;
  tags: string[];
  tagInput: string;
  errors: FormErrors;
  isSubmitting: boolean;
  successMessage: string;
  confidenceData: FieldConfidence[];
  showReadmeUpload: boolean;
  isAnalyzing: boolean;
}

export const EnhancedAddAPI: React.FC = () => {
  const navigate = useNavigate();
  
  const [state, setState] = useState<EnhancedAddAPIState>({
    formData: {
      name: '',
      description: '',
      url: '',
      status: 'active',
      version: '',
      documentation: '',
      authentication: '',
      rateLimit: '',
      payloadDescription: '',
      extractedFromReadme: false,
    },
    tags: [],
    tagInput: '',
    errors: {},
    isSubmitting: false,
    successMessage: '',
    confidenceData: [],
    showReadmeUpload: true,
    isAnalyzing: false,
  });

  const getConfidenceLevel = (score: number): 'high' | 'medium' | 'low' => {
    if (score >= 0.8) return 'high';
    if (score >= 0.5) return 'medium';
    return 'low';
  };

  const getConfidenceForField = (fieldName: string): FieldConfidence | undefined => {
    return state.confidenceData.find(c => c.field === fieldName);
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!state.formData.name?.trim()) {
      errors.name = 'Name is required';
    }

    if (!state.formData.description?.trim()) {
      errors.description = 'Description is required';
    }

    if (!state.formData.url?.trim()) {
      errors.url = 'URL is required';
    } else {
      try {
        new URL(state.formData.url);
      } catch {
        errors.url = 'Please enter a valid URL';
      }
    }

    setState(prev => ({ ...prev, errors }));
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: keyof EnhancedFormData, value: string) => {
    setState(prev => ({
      ...prev,
      formData: { ...prev.formData, [field]: value },
      errors: { ...prev.errors, [field]: undefined, general: undefined },
      successMessage: '',
      // Mark field as manually edited to remove confidence indicator
      confidenceData: prev.confidenceData.map(c => 
        c.field === field ? { ...c, isManuallyEdited: true } : c
      ),
    }));
  };

  const handleTagInputChange = (value: string) => {
    setState(prev => ({ ...prev, tagInput: value }));
  };

  const addTag = (tagValue: string) => {
    const trimmedTag = tagValue.trim();
    if (trimmedTag && !state.tags.includes(trimmedTag)) {
      setState(prev => ({
        ...prev,
        tags: [...prev.tags, trimmedTag],
        tagInput: '',
        // Mark tags as manually edited
        confidenceData: prev.confidenceData.map(c => 
          c.field === 'tags' ? { ...c, isManuallyEdited: true } : c
        ),
      }));
    }
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(state.tagInput);
    }
  };

  const handleTagInputBlur = () => {
    if (state.tagInput.includes(',')) {
      const newTags = state.tagInput
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag && !state.tags.includes(tag));
      
      setState(prev => ({
        ...prev,
        tags: [...prev.tags, ...newTags],
        tagInput: '',
        confidenceData: prev.confidenceData.map(c => 
          c.field === 'tags' ? { ...c, isManuallyEdited: true } : c
        ),
      }));
    } else if (state.tagInput.trim()) {
      addTag(state.tagInput);
    }
  };

  const removeTag = (tagToRemove: string) => {
    setState(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove),
      confidenceData: prev.confidenceData.map(c => 
        c.field === 'tags' ? { ...c, isManuallyEdited: true } : c
      ),
    }));
  };

  const handleReadmeAnalysisComplete = (analysisResult: any) => {
    const { extracted_data, confidence_scores } = analysisResult;
    
    setState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        name: extracted_data.name || prev.formData.name,
        description: extracted_data.description || prev.formData.description,
        url: extracted_data.url || prev.formData.url,
        payloadDescription: extracted_data.payloadDescription || prev.formData.payloadDescription,
        extractedFromReadme: true,
        readmeAnalysisConfidence: confidence_scores,
        originalReadmeContent: analysisResult.metadata?.originalName ? 
          `# ${extracted_data.name}\n\n${extracted_data.description}` : undefined,
      },
      tags: extracted_data.tags && extracted_data.tags.length > 0 ? extracted_data.tags : prev.tags,
      confidenceData: [
        { field: 'name', score: confidence_scores.name, isManuallyEdited: false },
        { field: 'description', score: confidence_scores.description, isManuallyEdited: false },
        { field: 'url', score: confidence_scores.url, isManuallyEdited: false },
        { field: 'tags', score: confidence_scores.tags, isManuallyEdited: false },
        { field: 'payloadDescription', score: confidence_scores.payloadDescription, isManuallyEdited: false },
      ],
      isAnalyzing: false,
    }));
  };

  const handleReadmeAnalysisError = (error: string) => {
    setState(prev => ({
      ...prev,
      errors: { ...prev.errors, general: error },
      isAnalyzing: false,
    }));
  };

  const acceptField = (fieldName: string) => {
    setState(prev => ({
      ...prev,
      confidenceData: prev.confidenceData.map(c => 
        c.field === fieldName ? { ...c, isManuallyEdited: false } : c
      ),
    }));
  };

  const rejectField = (fieldName: string) => {
    setState(prev => {
      const newFormData = { ...prev.formData };
      const newTags = [...prev.tags];
      
      if (fieldName === 'name') newFormData.name = '';
      else if (fieldName === 'description') newFormData.description = '';
      else if (fieldName === 'url') newFormData.url = '';
      else if (fieldName === 'payloadDescription') newFormData.payloadDescription = '';
      else if (fieldName === 'tags') newTags.length = 0;

      return {
        ...prev,
        formData: newFormData,
        tags: newTags,
        confidenceData: prev.confidenceData.filter(c => c.field !== fieldName),
      };
    });
  };

  const acceptAllSuggestions = () => {
    setState(prev => ({
      ...prev,
      confidenceData: prev.confidenceData.map(c => ({ ...c, isManuallyEdited: false })),
    }));
  };

  const rejectAllSuggestions = () => {
    setState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        name: '',
        description: '',
        url: '',
        payloadDescription: '',
      },
      tags: [],
      confidenceData: [],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setState(prev => ({ ...prev, isSubmitting: true, errors: {}, successMessage: '' }));

    try {
      const apiData: EnhancedFormData = {
        ...state.formData,
        tags: state.tags,
        createdBy: 'current-user@example.com', // This would come from auth context
      };

      const createdAPI = await createAPI(apiData);
      
      const successMsg = state.formData.extractedFromReadme 
        ? 'API created successfully with README analysis!'
        : 'API created successfully!';
      
      setState(prev => ({ 
        ...prev, 
        successMessage: successMsg,
        isSubmitting: false 
      }));

      // Navigate to the new API details page after a short delay
      setTimeout(() => {
        navigate(`/apis/${createdAPI.id}`);
      }, 1500);

    } catch (error) {
      setState(prev => ({
        ...prev,
        errors: { 
          general: error instanceof Error ? error.message : 'Failed to create API' 
        },
        isSubmitting: false,
      }));
    }
  };

  const renderConfidenceIndicator = (fieldName: string) => {
    const confidence = getConfidenceForField(fieldName);
    if (!confidence || confidence.isManuallyEdited) return null;

    const level = getConfidenceLevel(confidence.score);
    const percentage = Math.round(confidence.score * 100);

    return (
      <div className={`confidence-indicator confidence-${level}`} data-testid={`confidence-${fieldName}`}>
        <div className="confidence-score">
          {percentage}% confidence
        </div>
        <div className="confidence-actions">
          <button
            type="button"
            className="confidence-accept"
            onClick={() => acceptField(fieldName)}
            aria-label={`Accept ${fieldName}`}
          >
            ✓
          </button>
          <button
            type="button"
            className="confidence-reject"
            onClick={() => rejectField(fieldName)}
            aria-label={`Reject ${fieldName}`}
          >
            ✕
          </button>
        </div>
      </div>
    );
  };

  const hasAnyConfidenceData = state.confidenceData.some(c => !c.isManuallyEdited);

  return (
    <div className="add-api-container">
      <div className="add-api-header">
        <h1>Add New API</h1>
        <button 
          type="button" 
          className="back-button"
          onClick={() => navigate('/dashboard')}
        >
          ← Back to Dashboard
        </button>
      </div>

      {state.showReadmeUpload && (
        <ReadmeUpload
          onAnalysisComplete={handleReadmeAnalysisComplete}
          onError={handleReadmeAnalysisError}
          isLoading={state.isAnalyzing}
        />
      )}

      {hasAnyConfidenceData && (
        <div className="confidence-actions-panel">
          <h3>README Analysis Results</h3>
          <p>Review the extracted information and accept or reject suggestions:</p>
          <div className="bulk-actions">
            <button
              type="button"
              className="accept-all-button"
              onClick={acceptAllSuggestions}
            >
              Accept All Suggestions
            </button>
            <button
              type="button"
              className="reject-all-button"
              onClick={rejectAllSuggestions}
            >
              Reject All Suggestions
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="add-api-form">
        {state.errors.general && (
          <div className="error-message general-error" role="alert">
            {state.errors.general}
          </div>
        )}

        {state.successMessage && (
          <div className="success-message" role="alert">
            {state.successMessage}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="name">API Name *</label>
          <div className="input-with-confidence">
            <input
              type="text"
              id="name"
              value={state.formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={state.errors.name ? 'error' : ''}
              placeholder="Enter API name"
              required
            />
            {renderConfidenceIndicator('name')}
          </div>
          {state.errors.name && (
            <span className="error-text">{state.errors.name}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="description">Description *</label>
          <div className="input-with-confidence">
            <textarea
              id="description"
              value={state.formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className={state.errors.description ? 'error' : ''}
              placeholder="Describe what this API does"
              rows={4}
              required
            />
            {renderConfidenceIndicator('description')}
          </div>
          {state.errors.description && (
            <span className="error-text">{state.errors.description}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="url">API URL *</label>
          <div className="input-with-confidence">
            <input
              type="url"
              id="url"
              value={state.formData.url}
              onChange={(e) => handleInputChange('url', e.target.value)}
              className={state.errors.url ? 'error' : ''}
              placeholder="https://api.example.com"
              required
            />
            {renderConfidenceIndicator('url')}
          </div>
          {state.errors.url && (
            <span className="error-text">{state.errors.url}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="tags">Tags</label>
          <div className="input-with-confidence">
            <div className="tags-input-container">
              <div className="tags-display">
                {state.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="tag-remove"
                      aria-label={`Remove ${tag} tag`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                id="tags"
                value={state.tagInput}
                onChange={(e) => handleTagInputChange(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                onBlur={handleTagInputBlur}
                placeholder="Add tags (press Enter or use commas)"
              />
            </div>
            {renderConfidenceIndicator('tags')}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="payloadDescription">Payload Description</label>
          <div className="input-with-confidence">
            <textarea
              id="payloadDescription"
              value={state.formData.payloadDescription || ''}
              onChange={(e) => handleInputChange('payloadDescription', e.target.value)}
              placeholder="Describe the expected request/response format"
              rows={3}
            />
            {renderConfidenceIndicator('payloadDescription')}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={state.formData.status}
            onChange={(e) => handleInputChange('status', e.target.value as 'active' | 'inactive' | 'deprecated')}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="deprecated">Deprecated</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="version">Version</label>
          <input
            type="text"
            id="version"
            value={state.formData.version || ''}
            onChange={(e) => handleInputChange('version', e.target.value)}
            placeholder="e.g., v1.0.0"
          />
        </div>

        <div className="form-group">
          <label htmlFor="documentation">Documentation URL</label>
          <input
            type="url"
            id="documentation"
            value={state.formData.documentation || ''}
            onChange={(e) => handleInputChange('documentation', e.target.value)}
            placeholder="https://docs.example.com"
          />
        </div>

        <div className="form-group">
          <label htmlFor="authentication">Authentication</label>
          <input
            type="text"
            id="authentication"
            value={state.formData.authentication || ''}
            onChange={(e) => handleInputChange('authentication', e.target.value)}
            placeholder="e.g., Bearer Token, API Key"
          />
        </div>

        <div className="form-group">
          <label htmlFor="rateLimit">Rate Limit</label>
          <input
            type="text"
            id="rateLimit"
            value={state.formData.rateLimit || ''}
            onChange={(e) => handleInputChange('rateLimit', e.target.value)}
            placeholder="e.g., 1000/hour, 100/minute"
          />
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={state.isSubmitting}
          >
            {state.isSubmitting ? 'Creating API...' : 'Create API'}
          </button>
          <button
            type="button"
            className="cancel-button"
            onClick={() => navigate('/dashboard')}
            disabled={state.isSubmitting}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EnhancedAddAPI;
